import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { CustomInput } from 'reactstrap';
import { I18N, I18NContext } from 'commons';

const TimeRangeSelector = ({
  values,
  onChange,
  timeZone,
  quietTimeId
}) => {
  const i18n = useContext(I18NContext);


  const [timeValues, updateTimeValues] = useState(values || {
    fromHour: 0,
    fromMin: 0,
    toHour: 0,
    toMin: 0
  });
  const [enable24Hours, setEnable24Hours] = useState(`${timeValues.fromHour}${timeValues.fromMin}` === `${timeValues.toHour}${timeValues.toMin}` || false);

  const renderOptions = (size) => {
    const options = [];
    for (let i = 0; i < size; i++) {
      options.push(React.createElement('option', {
        value: i
      }, (i < 10) ? `0${i}` : i.toString()));
    }
    return options;
  };

  const handleChange = (fieldName, event) => {
    const val = event.currentTarget.value;
    const newValues = {};
    newValues[fieldName] = val;
    if (enable24Hours) {
      if (fieldName === 'fromHour') {
        newValues.toHour = val;
      }
      if (fieldName === 'fromMin') {
        newValues.toMin = val;
      }
    }
    const updatedData = { ...timeValues, ...newValues };
    updateTimeValues(updatedData);
    onChange(updatedData);
  };

  const handle24HourChange = () => {
    if (!enable24Hours) {
      const { fromHour, fromMin } = timeValues;
      const updatedData = {
        fromHour,
        fromMin,
        toHour: fromHour,
        toMin: fromMin
      };
      updateTimeValues(updatedData);
      onChange(updatedData);
    }
    setEnable24Hours(!enable24Hours);
  };

  return (
    <div>
      <div className="row">
        <div className="col-12 col-lg-5">
          <label className="mr-4" id="quietTimeTimeLabel" htmlFor="quietTimeTimeSection">
            <I18N>contact.path.device.time.frame.quiet.time.time</I18N>
          </label>
          <div className="d-inline-block">
            <input
              type="checkbox"
              id="enable24hours"
              value={enable24Hours}
              checked={enable24Hours}
              onChange={handle24HourChange}
              aria-describedby="quietTimeTimeLabel"
            />
            <label htmlFor="enable24hours"><I18N>contact.path.device.time.frame.quiet.time.24hours</I18N></label>
          </div>
        </div>
        <div className="col-12 col-lg-7">
          <div className="d-flex flex-column left mr-4">
            <div className="d-inline">
              <label id="from-time-label" htmlFor="fromTime"><I18N>contact.path.device.time.frame.quiet.time.from</I18N></label>
            </div>
            <div className="d-flex flex-row" id="fromTime" role="group" aria-labelledby="from-time-label" aria-controls="fromHourSelect fromMinSelect">
              <div className='d-flex flex-column'>
                <select
                  name="fromHour"
                  id={`fromHour-${quietTimeId}`}
                  value={timeValues.fromHour || 0}
                  onChange={(e) => { handleChange('fromHour', e); }}
                  aria-describedby="from-time-label from-time-hour-label"
                >
                  {
                    renderOptions(24)
                  }
                </select>
                <label id="from-time-hour-label" htmlFor={`fromHour-${quietTimeId}`} className='time-range-label'>
                  <span className='sr-only'><I18N>contact.path.device.time.frame.quiet.time.from</I18N></span>
                  <I18N>component.time.range.selector.label.hour</I18N>
                </label>
              </div>
              <span className="mx-1">:</span>
              <div className='d-flex flex-column'>
                <select
                  name="fromMin"
                  id={`fromMin-${quietTimeId}`}
                  value={timeValues.fromMin || 0}
                  onChange={(e) => { handleChange('fromMin', e); }}
                  aria-describedby="from-time-label from-time-minute-label"
                >
                  { renderOptions(60) }
                </select>
                <label id="from-time-minute-label" htmlFor={`fromMin-${quietTimeId}`} className='time-range-label'>
                  <span className='sr-only'><I18N>contact.path.device.time.frame.quiet.time.from</I18N></span>
                  <I18N>component.time.range.selector.label.minute</I18N></label>
              </div>
              
            </div>
          </div>
          <div className="d-flex flex-column mr-4">
            <div className="d-inline">
              <label id="to-time-label" htmlFor="toTime"><I18N>contact.path.device.time.frame.quiet.time.to</I18N></label>
            </div>
            <div className="d-flex flex-row" id="toTime" role="group" aria-labelledby="to-time-label" aria-controls="toHourSelect toMinSelect">
              <div className='d-flex flex-column'>
                <select
                  name="toHour"
                  id={`toHour-${quietTimeId}`}
                  disabled={enable24Hours}
                  value={timeValues.toHour || 0}
                  onChange={(e) => { handleChange('toHour', e); }}
                  aria-describedby="to-time-label to-time-hour-label"
                >
                  { renderOptions(24) }
                </select>
                <label id="to-time-hour-label" htmlFor={`toHour-${quietTimeId}`} className='time-range-label'>
                  <span className='sr-only'><I18N>contact.path.device.time.frame.quiet.time.to</I18N></span>
                  <I18N>component.time.range.selector.label.hour</I18N></label>
              </div>
              <span className="mx-1">:</span>
              <div className='d-flex flex-column'>
                <select
                  name="toMin"
                  id={`toMin-${quietTimeId}`}
                  disabled={enable24Hours}
                  value={timeValues.toMin || 0}
                  onChange={(e) => { handleChange('toMin', e); }}
                  aria-describedby="to-time-label to-time-minute-label"
                >
                  { renderOptions(60) }
                </select>
                <label id="to-time-minute-label" htmlFor={`toMin-${quietTimeId}`} className='time-range-label'>
                  <span className='sr-only'><I18N>contact.path.device.time.frame.quiet.time.to</I18N></span>
                  <I18N>component.time.range.selector.label.minute</I18N></label>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12 text-black-50">
          <span id="time-range-selector-timezone-label"><I18N>contact.path.device.time.frame.quiet.time.timezone</I18N></span>
          <span className="ml-2" id="quietTimeTimeZone">{timeZone}</span>
        </div>
      </div>
    </div>
  );
};

TimeRangeSelector.propTypes = {
  values: PropTypes.shape(PropTypes.object),
  onChange: PropTypes.func.isRequired,
  timeZone: PropTypes.string
};

TimeRangeSelector.defaultProps = {
  values: {
    fromHour: 0,
    fromMin: 0,
    toHour: 0,
    toMin: 0
  },
  timeZone: ''
};

export default TimeRangeSelector;
